import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import RadioInput from 'LuxKit/Radio/RadioInput'
import BodyText from 'LuxKit/Typography/BodyText'
import TourPriceToggle from 'components/Tours/TourPriceToggle'
import Group from 'components/utils/Group'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import useQueryParams from 'hooks/useQueryParams'
import useToggle from 'hooks/useToggle'
import { rem } from 'polished'
import React, { useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

const RadioGroup = styled(Group)`
  padding-inline: ${rem(16)};
`

interface Props {
  fetching?: boolean | undefined
  totalNumberOfOffers: number
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

function TourSearchSortHeader(props: Props) {
  const { fetching, totalNumberOfOffers, onChange } = props
  const intl = useIntl()
  const { tourSortByTypes } = useOfferListSortOptions()
  const [open, toggleSort, _, closeSort] = useToggle()

  const queryParams = useQueryParams()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const sortParam = useMemo(() => queryParams.get('sortBy'), [queryParams])
  const selectedOption = useMemo(() =>
    tourSortByTypes.find(o => o.value === sortParam) ?? tourSortByTypes[0],
  [sortParam, tourSortByTypes])

  const onSortChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e)
    }
    closeSort()
  }

  const shouldShow = !fetching && totalNumberOfOffers > 0

  if (!shouldShow) {
    return null
  }

  return (
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
      <BodyText variant="large">
        {intl.formatMessage(
          {
            id: 'vHuEORMyz7',
            defaultMessage: 'Showing {count, plural, one {# tour} other {# tours}}',
            description: 'Tour search results count showing total number of tours found',
          },
          { count: totalNumberOfOffers },
        )}
      </BodyText>
      <Group direction="horizontal" gap={8}>
        <TourPriceToggle />
        <AssistChip
          variant="filled"
          size="medium"
          onClick={toggleSort}
          endIcon={<DropdownChevron open={open} />}
          ref={triggerRef}
        >
          {intl.formatMessage(
            {
              id: 'NrGsT3iWJJ',
              defaultMessage: 'Sort: {sortOption}',
              description: 'Sort chip label showing current sort option for tour search',
            },
            { sortOption: selectedOption?.label },
          )}
        </AssistChip>
        <DropdownList
          size="S"
          open={open}
          onClose={closeSort}
          anchorRef={triggerRef}
          triggerRef={triggerRef}
          data-testid="sort-options-dropdown"
        >
          <RadioGroup direction="vertical" gap={16}>
            {tourSortByTypes.map(option => <RadioInput
              key={option.value}
              checked={selectedOption?.value === option.value}
              onChange={onSortChange}
              name="sort"
              value={option.value}
            >
              {option.label}
            </RadioInput>)}
          </RadioGroup>
        </DropdownList>
      </Group>
    </Group>
  )
}

export default TourSearchSortHeader
